import type { ComposedState } from '../state';
import {
    HubPersona,
    ContactFolder,
    createSelector,
    isContactsOrContactListsLeftNavItem,
    favoritesLeftNavItemId,
    allPeopleFolderId,
    range,
    groupsFolderId,
} from '../../shared';
import type { HubPersonasMap, UnifiedGroupsMap } from '../../appstate';
import { QueryData, QuerySource } from '../models/query';
import { searchPeopleLocally } from '../actions/data/searchResultActionsUtils';
import { mergeAndExpandResults } from '../helpers/searchResultUtils';

export type { HubPersona };

const MAX_SUGGESTION_COUNT_INCLUDING_HISTORY = 6;

/**
 * Retrieves the current queryData
 */
export const getQueryData = createSelector<ComposedState, string, string, ContactFolder, QueryData>(
    'getQueryData',
    [
        (state: ComposedState) => state.appState.data.searchState.currentFolderId,
        (state: ComposedState) => state.packageState.searchBoxText,
        (state: ComposedState) => state.appState.data.contactFoldersState.rootContactFolder,
    ],
    (folderId, searchBoxText, rootFolder) => {
        let internalFolderId = folderId;

        // "contactLists" and "contacts" does a search in the root folder
        if (
            rootFolder &&
            (isContactsOrContactListsLeftNavItem(folderId) || folderId === allPeopleFolderId)
        ) {
            internalFolderId = rootFolder.id;
        }

        const querySource = getQuerySource(internalFolderId);

        return {
            currentQueryString: searchBoxText,
            folderId: folderId,
            internalFolderId: internalFolderId,
            isAllFolder: rootFolder && folderId === rootFolder.id,
            source: querySource,
        };
    }
);

export const getRecipientCacheSuggestions = createSelector<
    ComposedState,
    QueryData,
    { [query: string]: string[] },
    HubPersonasMap,
    UnifiedGroupsMap,
    HubPersona[]
>(
    'getRecipientCacheSuggestions',
    [
        getQueryData,
        (state: ComposedState) => state.appState.data.searchState.recipientCacheSuggestions,
        (state: ComposedState) => state.appState.data.personas,
        (state: ComposedState) => state.appState.data.groupsHubState.unifiedGroupsMap,
    ],
    (queryData, recipientCacheSuggestions, hubPersonasMap, groupsMap) => {
        const hubPersonas: HubPersona[] = Object.keys(hubPersonasMap).map(
            key => hubPersonasMap[key]
        );

        // Grab RC entries for queryString. If they are still loading, display entries for as much of the queryString as has been loaded
        // (This reduces flickering during typing)
        const queryString = queryData.currentQueryString;
        // Turns "Query" into ["Query", "Quer", "Que", "Qu", "Q", ""]
        const prefixes = range(queryString.length + 1).map(i =>
            queryString.substring(0, queryString.length - i)
        );
        const recipientCacheSuggestionsForQuery = prefixes
            .map(prefix =>
                recipientCacheSuggestions.hasOwnProperty(prefix)
                    ? recipientCacheSuggestions[prefix]
                    : undefined
            )
            .find(suggestions => !!suggestions);

        return mergeAndExpandResults(
            hubPersonasMap,
            groupsMap,
            recipientCacheSuggestionsForQuery,
            !!recipientCacheSuggestionsForQuery,
            searchPeopleLocally(queryData, hubPersonas)
        ).slice(0, MAX_SUGGESTION_COUNT_INCLUDING_HISTORY);
    }
);

/**
 * Retrieve the current persona, only if originated by a suggestion click
 */
export const getClickedSearchSuggestion = createSelector<
    ComposedState,
    string,
    HubPersonasMap,
    HubPersona
>(
    'getClickedSearchSuggestion',
    [
        (state: ComposedState) => state.packageState.personaPillId,
        (state: ComposedState) => state.appState.data.personas,
    ],
    (personaPillId, personasMap) => {
        if (personaPillId) {
            return personasMap[personaPillId];
        } else {
            return null;
        }
    }
);

// Determine query source
function getQuerySource(internalFolderId: string): QuerySource {
    switch (internalFolderId) {
        case favoritesLeftNavItemId:
            return QuerySource.Favorites;
        case groupsFolderId:
            return QuerySource.Groups;
        default:
            return QuerySource.Folder;
    }
}
export type { ClickedHubPersonaState } from '../../appstate';
